import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard,
    Megaphone,
    Ticket,
    ClipboardList,
    AlertTriangle,
    Gavel,
    Wrench,
    LogOut,
    Sparkles,
    Layers,
    Scale,
    ShieldCheck,
    ClipboardCheck,
    Star,
    BarChart3,
    PieChart,

    Hammer,
    // Archive removed due to build error
    CheckCircle,
    User,
    Users,
    Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useUnreadAnnouncements } from '../../hooks/useUnreadAnnouncements';
import { useTickets } from '../../contexts/TicketContext';
import { theme } from '../../lib/theme';
import {
    BASIC_STAFF_ROLES,
    ADVANCED_STAFF_ROLES,
    CONTENT_CREATOR_ROLES,
    ALL_STAFF_ROLES
} from '../../utils/permissions';

import gtaCharacter from '../../assets/gta_character.png';
import gtaCar from '../../assets/gta_car.png';
import gtaGun from '../../assets/gta_gun.png';
import gtaHouse from '../../assets/gta_house.png';
import logoCapital from '../../assets/logo-capital.png';

import NotificationBell from '../Dashboard/NotificationBell';

import { getStaffDisplayName } from '../../utils/staffDisplay';

const StaffSidebar = ({ onClose }) => {
    const { user, logout } = useAuth();
    const { tickets } = useTickets();
    const { unreadCount, hasUnread } = useUnreadAnnouncements();
    const navigate = useNavigate();
    const location = useLocation();
    const [logoIndex, setLogoIndex] = useState(0);
    const [titleIndex, setTitleIndex] = useState(0);
    const logos = [gtaCharacter, gtaCar, gtaGun, gtaHouse, logoCapital];

    const formatRole = (role) => {
        if (!role) return '';
        return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };



    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const { refreshTickets } = useTickets();

    useEffect(() => {
        const timer = setInterval(() => {
            refreshTickets(false); // Silent refresh
        }, 10000);
        return () => clearInterval(timer);
    }, [refreshTickets]);

    const greeting = React.useMemo(() => {
        const hour = currentTime.getHours();
        const minutes = currentTime.getMinutes();
        const time = hour + minutes / 60;

        const allGreetings = [
            { start: 0, end: 3, text: 'BOA MADRUGADA' },
            { start: 3, end: 5, text: 'QUASE AMANHECENDO' },
            { start: 5, end: 6, text: 'INSÔNIA CRIATIVA' },
            { start: 6, end: 8, text: 'BOM DIA' },
            { start: 8, end: 12, text: 'BOM DIA' },
            { start: 12, end: 14, text: 'ALMOÇO' },
            { start: 14, end: 18, text: 'BOA TARDE' },
            { start: 18, end: 24, text: 'BOA NOITE' }
        ];

        const match = allGreetings.find(g => time >= g.start && time < g.end);
        return match?.text || 'BOA NOITE';
    }, [currentTime]);

    const formattedTime = currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    const staffCodename = getStaffDisplayName(user);

    const titles = [
        'Grupo Capital',
        'Staff Panel',
        staffCodename,
        formatRole(user?.accessLevel),
        formattedTime,
        greeting
    ];

    const newTicketsCount = tickets?.filter(t =>
        t.status === 'aberto' &&
        !t.assignedTo &&
        t.type !== 'escalation' &&
        (!t.responses || !t.responses.some(r => r?.isStaff))
    ).length || 0;

    useEffect(() => {
        if (logoIndex < logos.length - 1) {
            const timer = setTimeout(() => {
                setLogoIndex(prev => prev + 1);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [logoIndex]);

    useEffect(() => {
        const timer = setInterval(() => {
            setTitleIndex((prev) => (prev + 1) % titles.length);
        }, 20000);
        return () => clearInterval(timer);
    }, [titles.length]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const contractSigned = user?.staffDetails?.contractSigned;

    const allMenuItems = [
        {
            icon: ShieldCheck,
            label: 'Dashboard',
            path: '/staff',
            gradient: 'from-red-600 to-orange-600',
            color: theme.colors.accent.red,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: Ticket,
            label: 'Novos Tickets',
            path: '/staff/new-tickets',
            gradient: 'from-emerald-600 to-teal-600',
            color: theme.colors.accent.green,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: ClipboardList,
            label: 'Meus Tickets',
            path: '/staff/your-tickets',
            gradient: 'from-blue-600 to-indigo-600',
            color: theme.colors.accent.blue,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: AlertTriangle,
            label: 'Escalação',
            path: '/staff/escalations',
            gradient: 'from-orange-600 to-red-600',
            color: theme.colors.accent.orange,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: Layers,
            label: 'Todos os Tickets',
            path: '/staff/all-tickets',
            gradient: 'from-indigo-600 to-violet-600',
            color: theme.colors.accent.purple,
            allowedRoles: BASIC_STAFF_ROLES
        },
        { type: 'separator' },
        {
            icon: Hammer,
            label: 'Ferramenta Staff',
            path: '/staff/tools',
            gradient: 'from-indigo-600 to-purple-600',
            color: theme.colors.accent.purple,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: PieChart,
            label: 'Estatísticas',
            path: '/staff/statistics',
            gradient: 'from-fuchsia-600 to-pink-600',
            color: theme.colors.accent.pink,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: ClipboardList,
            label: 'Prestação e Contrato',
            path: '/staff/service-provision',
            gradient: 'from-pink-600 to-rose-600',
            color: theme.colors.accent.pink,
            allowedRoles: ALL_STAFF_ROLES
        },
        { type: 'separator' },
        {
            icon: ClipboardCheck,
            label: 'Triagem de Tickets',
            path: '/staff/triagem',
            gradient: 'from-amber-600 to-orange-600',
            color: theme.colors.accent.orange,
            allowedRoles: BASIC_STAFF_ROLES
        },
        {
            icon: ShieldCheck,
            label: 'Controle',
            path: '/staff/quality-control',
            gradient: 'from-blue-600 to-indigo-600',
            color: theme.colors.accent.blue,
            allowedRoles: ADVANCED_STAFF_ROLES
        },
        {
            icon: Users,
            label: 'Gestão da Equipe',
            path: '/staff/team-management',
            gradient: 'from-emerald-500 to-green-600',
            color: theme.colors.accent.green,
            allowedRoles: ADVANCED_STAFF_ROLES
        },
        {
            icon: Users,
            label: 'Revisão Staff',
            path: '/staff/review',
            gradient: 'from-purple-600 to-indigo-600',
            color: theme.colors.accent.purple,
            allowedRoles: ['admin', 'head_staff', 'head_suporte', 'head_moderacao', 'analista_qualidade']
        },
        {
            icon: BarChart3,
            label: 'Relatório',
            path: '/staff/reports',
            gradient: 'from-cyan-600 to-blue-600',
            color: theme.colors.accent.blue,
            allowedRoles: ADVANCED_STAFF_ROLES
        },
        { type: 'separator' },
        {
            icon: Shield,
            label: 'Painel Admin',
            path: '/admin',
            gradient: 'from-red-600 to-orange-600',
            color: theme.colors.accent.red,
            allowedRoles: ['admin']
        },
        {
            icon: User,
            label: 'Painel do Usuário',
            path: '/dashboard/user',
            gradient: 'from-blue-500 to-cyan-500',
            color: theme.colors.accent.blue,
            allowedRoles: ['*']
        }
    ];

    const menuItems = React.useMemo(() => {
        // First filter by roles
        let filtered = allMenuItems.filter(item => {
            if (item.type === 'separator') return true;
            if (item.allowedRoles?.includes('*')) return true;
            return item.allowedRoles?.includes(user?.accessLevel);
        });

        // High-level roles bypass contract check
        const exemptRoles = ['admin', 'direcao', 'head_staff', 'head_moderacao', 'head_suporte', 'analista_qualidade'];
        if (exemptRoles.includes(user?.accessLevel)) {
            return filtered;
        }

        // Filter based on contract status (if not exempt)
        if (!contractSigned) {
            filtered = filtered.filter(item => item.path === '/staff/service-provision' || item.path === '/dashboard');
        }

        // Specific case for Criador de Conteúdo is already handled by role filter above
        // but we can ensure they only see what's specified
        if (user?.accessLevel === 'criador_de_conteudo') {
            filtered = filtered.filter(item =>
                item.path === '/staff/service-provision' ||
                item.path === '/dashboard' ||
                item.type === 'separator'
            );
        }

        // Jurídico is also handled by role filter, but let's keep it consistent
        if (user?.accessLevel === 'departamento_juridico') {
            filtered = filtered.filter(item =>
                item.path === '/staff' ||
                item.path === '/staff/service-provision' ||
                item.path === '/staff/tools' ||
                item.path === '/staff/juridico' ||
                item.path === '/dashboard' ||
                item.type === 'separator'
            );
        }

        // Clean up double separators
        return filtered.filter((item, idx, arr) => {
            if (item.type !== 'separator') return true;
            const nextItem = arr[idx + 1];
            if (!nextItem || nextItem.type === 'separator') return false;
            return true;
        });
    }, [contractSigned, user?.accessLevel]);

    return (
        <div
            className="w-72 flex flex-col h-full bg-black/90 backdrop-blur-xl border-r border-white/10 lg:fixed lg:left-0 z-50 transition-all duration-300"
            style={{
                top: 'var(--banner-height, 0px)',
                height: 'calc(100vh - var(--banner-height, 0px))'
            }}
        >
            {/* Header da Sidebar */}
            <div className="p-4 border-b border-white/10 flex flex-col items-center relative">
                {/* Notification Bell - Absolute Top Right */}
                <div className="absolute top-4 right-4 z-50">
                    <NotificationBell />
                </div>

                {/* Animated Logo Container */}
                <div className="relative w-24 h-24 mb-3">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={logoIndex}
                            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            exit={{ opacity: 0, scale: 1.1, rotate: 10 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0 rounded-full overflow-hidden border-4 border-white/10 shadow-[0_0_20px_rgba(59,130,246,0.2)] bg-gradient-to-br from-blue-500/20 to-purple-500/20"
                        >
                            <img
                                src={logos[logoIndex]}
                                alt={`Logo sequence ${logoIndex}`}
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </AnimatePresence>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" />
                </div>

                <div className="text-center mb-3 h-12 flex flex-col justify-center w-full">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={titleIndex}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center justify-center gap-2 w-full"
                        >
                            <h2 className="text-lg font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent truncate w-full px-2">
                                {titles[titleIndex]}
                            </h2>
                            {titleIndex === 0 && <Sparkles className="h-4 w-4 text-yellow-400 animate-pulse flex-shrink-0" />}
                            {titleIndex === 2 && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Informações do Staff - More Compact */}
                <div className="w-full px-3 py-2 bg-white/5 rounded-xl border border-white/5 flex items-center justify-center">
                    <div className="text-xs text-white font-bold truncate">
                        {staffCodename}
                    </div>
                </div>

                {/* Comunicados button */}
                {(() => {
                    const exemptRoles = ['admin', 'direcao', 'head_staff', 'head_moderacao', 'head_suporte', 'analista_qualidade'];
                    const isExempt = exemptRoles.includes(user?.accessLevel);
                    const canSee = (contractSigned || isExempt) && BASIC_STAFF_ROLES.includes(user?.accessLevel);

                    if (!canSee) return null;

                    return (
                        <button
                            onClick={() => { navigate('/staff/announcements'); onClose && onClose(); }}
                            className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 relative group overflow-hidden ${location.pathname === '/staff/announcements'
                                ? 'bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-white border border-purple-500/40 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                                : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/5'
                                }`}
                        >
                            <div className="relative flex items-center gap-2 z-10">
                                <Megaphone
                                    className={`h-[17px] w-[17px] transition-colors ${hasUnread ? 'animate-bounce text-purple-400' : 'text-gray-400 group-hover:text-purple-300'}`}
                                />
                                <span className="text-[14px] font-bold tracking-wide">COMUNICADOS</span>
                                {hasUnread && (
                                    <Badge className="ml-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] h-4 min-w-4 px-1 flex items-center justify-center border-0 shadow-lg shadow-purple-500/20">
                                        {unreadCount}
                                    </Badge>
                                )}
                            </div>
                        </button>
                    );
                })()}
            </div>

            {/* Navegação - Compact and No Scrollbar */}
            <nav className="flex-1 p-3 overflow-y-auto scrollbar-none space-y-1">
                <style dangerouslySetInnerHTML={{
                    __html: `
                    .scrollbar-none::-webkit-scrollbar { display: none; }
                    .scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }
                `}} />
                {menuItems.map((item, index) => {
                    if (item.type === 'separator') {
                        return (
                            <div key={`sep-${index}`} className="my-2 border-t border-white/10" />
                        );
                    }

                    const Icon = item.icon;
                    // Check if current location matches item path perfectly (including query) 
                    // OR if it's the dashboard root path
                    const currentPath = location.pathname + location.search;
                    const isActive = currentPath === item.path ||
                        (item.path === '/staff' && location.pathname === '/staff') ||
                        (location.pathname === item.path && !item.path.includes('?'));

                    return (
                        <div key={item.path}>
                            <button
                                onClick={() => {
                                    if (item.path === '/dashboard') {
                                        // Force full navigation for Dashboard to ensure cleaner context switch
                                        window.location.href = '/dashboard';
                                    } else {
                                        navigate(item.path);
                                    }
                                    onClose && onClose();
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all duration-300 group relative overflow-hidden ${isActive
                                    ? 'bg-gradient-to-r from-white/10 to-transparent text-white border border-white/10'
                                    : 'text-gray-400/80 hover:bg-white/5 hover:text-white'
                                    }`}
                            >
                                <div className="flex items-center space-x-3 relative z-10">
                                    <div className={`p-1.5 rounded-lg transition-all duration-300 ${isActive
                                        ? `bg-gradient-to-br ${item.gradient} shadow-lg shadow-blue-500/10`
                                        : 'bg-white/5 group-hover:bg-white/10'
                                        }`}>
                                        {Icon && <Icon
                                            className={`h-[17px] w-[17px] transition-colors ${isActive ? 'text-white' : 'text-gray-400/80 group-hover:text-white'}`}
                                        />}
                                    </div>
                                    <span className={`text-[16px] font-medium ${isActive ? 'text-white' : 'group-hover:text-white'}`}>
                                        {item.label}
                                    </span>
                                    {item.label === 'Novos Tickets' && newTicketsCount > 0 && (
                                        <Badge className="ml-auto bg-blue-500 text-white text-[11px] h-[18px] min-w-[18px] px-1 flex items-center justify-center border-0 shadow-lg shadow-blue-500/20 animate-pulse">
                                            {newTicketsCount}
                                        </Badge>
                                    )}
                                </div>

                                {isActive && (
                                    <motion.div
                                        layoutId="active-indicator"
                                        className={`absolute right-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-l-full bg-gradient-to-b ${item.gradient}`}
                                    />
                                )}
                            </button>
                        </div>
                    );
                })}
            </nav>

            {/* Footer da Sidebar */}
            <div className="p-3 border-t border-white/10">
                <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full justify-center gap-2 text-gray-400 hover:text-white hover:bg-red-500/10 transition-all duration-300 rounded-xl group h-10"
                >
                    <LogOut className="h-[17px] w-[17px] transition-transform group-hover:-translate-x-1" />
                    <span className="font-bold tracking-wide text-[12px]">SAIR DO PAINEL</span>
                </Button>
            </div>
        </div>
    );
};

export default StaffSidebar;
